import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fetchPdfBlob, printPdfBlob } from "@/lib/printPdf";
import { toast } from "sonner";
import { formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileText, EnvelopeSimple, ClockCounterClockwise, Flag } from "@phosphor-icons/react";

const err = (e) => toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen");
export const openDoc = async (id, version) => {
  try { const { blob } = await fetchPdfBlob(`/documents/${id}/download${version ? `?version=${version}` : ""}`); window.open(URL.createObjectURL(blob), "_blank"); }
  catch (e) { toast.error(e.message); }
};
const ICON = { history: ClockCounterClockwise, document: FileText, communication: EnvelopeSimple };

export function RequestTimeline({ rid, reloadKey }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/rent-increase/requests/${rid}/timeline`).then(({ data }) => setD(data)); }, [rid, reloadKey]);
  if (!d) return <div className="text-[11px] text-slate-400">Lade Verlauf…</div>;
  return (
    <div className="mt-1.5 space-y-2" data-testid={`ri-timeline-${rid}`}>
      {d.deadlines.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {d.deadlines.map((x) => (
            <span key={x.key} className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] ${x.passed ? "border-slate-200 text-slate-400" : "border-amber-200 bg-amber-50 text-amber-900"}`} data-testid={`ri-deadline-${x.key}`}>
              <Flag size={11} /> {x.label}: {formatDate(x.date)}{x.passed ? " (abgelaufen)" : ""}
            </span>
          ))}
        </div>
      )}
      <ol className="space-y-1 border-l border-slate-200 pl-3">
        {d.items.map((i, k) => {
          const Icon = ICON[i.type] || FileText;
          return (
            <li key={k} className="text-[11px] text-slate-700 flex gap-1.5" data-testid={`ri-tl-${i.type}-${k}`}>
              <Icon size={12} className="mt-0.5 shrink-0 text-slate-400" />
              <span><span className="text-slate-400">{formatDate(i.at)} {String(i.at || "").slice(11, 16)}{i.by ? ` · ${i.by}` : ""}</span> — {i.title}
                {i.type === "communication" && <span className="text-slate-400"> ({i.direction === "in" ? "eingehend" : "ausgehend"}, {i.kind})</span>}
                {i.document_id && <button type="button" className="ml-1 text-teal-700 hover:underline" onClick={() => openDoc(i.document_id, i.version)}
                  title={i.version === "unsigned" ? "Ursprüngliches Schreiben (ohne Unterschrift) zum erneuten Ausdrucken" : i.action === "letter_filed" ? "Unterschriebenes Original (Scan)" : undefined}
                  data-testid={`ri-tl-open-${k}`}>{i.version === "unsigned" ? "PDF zum Drucken" : i.action === "letter_filed" ? "Original öffnen" : "öffnen"}</button>}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function LinkDialog({ req, tenancyId, onClose, onDone }) {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState({});
  useEffect(() => { api.get(`/rent-increase/tenancy/${tenancyId}/linkable`).then(({ data }) => setD(data)); }, [tenancyId]);
  const save = async () => {
    const ids = Object.keys(sel).filter((k) => sel[k]);
    try {
      await api.post(`/rent-increase/requests/${req.id}/link`, { document_ids: ids.filter((x) => x.startsWith("d:")).map((x) => x.slice(2)), communication_ids: ids.filter((x) => x.startsWith("c:")).map((x) => x.slice(2)) });
      toast.success("Zugeordnet – Kategorie „Mieterhöhung“"); onDone(); onClose();
    } catch (e) { err(e); }
  };
  const Row = ({ k, title, sub }) => (
    <label className="flex items-start gap-2 py-1 border-t border-slate-100 text-xs cursor-pointer">
      <Checkbox checked={!!sel[k]} onCheckedChange={(v) => setSel((s) => ({ ...s, [k]: !!v }))} data-testid={`ri-link-${k}`} />
      <span>{title}<div className="text-[10px] text-slate-500">{sub}</div></span>
    </label>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ri-link-dialog">
        <DialogHeader><DialogTitle>Dokument / Schriftverkehr zuordnen</DialogTitle>
          <DialogDescription>Zugeordnete Einträge erhalten die Kategorie „Mieterhöhung“, bleiben im Reiter Dokumente bzw. Kommunikation und erscheinen zusätzlich in der Akte dieses Vorgangs.</DialogDescription></DialogHeader>
        {!d ? <div className="text-sm text-slate-500">Lade…</div> : (
          <div className="grid md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
            <div><div className="text-[11px] uppercase text-slate-500 mb-1">Dokumente & Scans</div>
              {d.documents.map((x) => <Row key={x.id} k={`d:${x.id}`} title={x.title} sub={`${formatDate(x.created_at)} · ${x.category || "—"}`} />)}
              {!d.documents.length && <div className="text-xs text-slate-400">Keine</div>}</div>
            <div><div className="text-[11px] uppercase text-slate-500 mb-1">Schriftverkehr (Mail, Brief, Notiz)</div>
              {d.communications.map((x) => <Row key={x.id} k={`c:${x.id}`} title={x.subject} sub={`${formatDate(x.occurred_at)} · ${x.direction === "in" ? "eingehend" : "ausgehend"} · ${x.kind}`} />)}
              {!d.communications.length && <div className="text-xs text-slate-400">Keine</div>}</div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Abbrechen</Button><Button onClick={save} data-testid="ri-link-save">Zuordnen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LawyerDialog({ req, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [law, setLaw] = useState(null);
  const [cc, setCc] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.length < 2) { setHits([]); return undefined; }
    const t = setTimeout(() => api.get("/contacts", { params: { q, limit: 8 } }).then(({ data }) => setHits(Array.isArray(data) ? data : data.items || [])), 250);
    return () => clearTimeout(t);
  }, [q]);
  const download = async () => {
    setBusy(true);
    try { const res = await api.post(`/rent-increase/requests/${req.id}/lawyer-package`, {}, { responseType: "blob" }); printPdfBlob(res.data); onDone(); }
    catch (e) { err(e); }
    setBusy(false);
  };
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/rent-increase/requests/${req.id}/lawyer-package`, { lawyer_contact_id: law.id, cc_owner: cc, note }); toast.success(`Akte an ${data.to} gesendet${data.owner ? `, Eigentümer in Kenntnis` : ""}`); onDone(); onClose(); }
    catch (e) { err(e); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ri-lawyer-dialog">
        <DialogHeader><DialogTitle>Akte für Anwalt (Zustimmungsklage)</DialogTitle>
          <DialogDescription>Gesamt-PDF mit Deckblatt (Übersicht, Fristen), Verlauf, Schreiben, Zugangsnachweisen und zugeordnetem Schriftverkehr. Klagefrist: {formatDate(req.klage_deadline)}.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div><Label className="text-xs">Rechtsanwalt aus Kontakten</Label>
            {law ? <div className="flex items-center gap-2 text-sm" data-testid="ri-lawyer-selected">{law.display_name || law.name}<button type="button" className="text-xs text-slate-500 hover:underline" onClick={() => setLaw(null)}>ändern</button></div> : (
              <><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name oder Kanzlei suchen…" data-testid="ri-lawyer-search" />
                {hits.map((c) => <button key={c.id} type="button" onClick={() => setLaw(c)} className="block w-full text-left text-xs px-2 py-1 hover:bg-slate-50" data-testid={`ri-lawyer-hit-${c.id}`}>{c.display_name || c.name}</button>)}</>
            )}</div>
          <label className="flex items-center gap-2 text-xs"><Checkbox checked={cc} onCheckedChange={(v) => setCc(!!v)} data-testid="ri-lawyer-cc-owner" /> Eigentümer zur Kenntnisnahme informieren</label>
          <div><Label className="text-xs">Nachricht</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="ri-lawyer-note" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={download} disabled={busy} data-testid="ri-lawyer-download">PDF herunterladen</Button><Button onClick={send} disabled={busy || !law} data-testid="ri-lawyer-send">An Anwalt senden</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
